import { HeightMode } from '@wix/ambassador-app-service-webapp/types';
import {
  WidgetSize,
  DefaultPresetKeys,
  PresetsOptions,
} from '../../../../types/unifiedComponents';
import {
  InitialSizeSetting,
  ComponentInitialSize,
  StaticContainer,
} from '@wix/ambassador-devcenter-components-v1-component/types';
import {
  CompLayout,
  Point,
  UnitSize,
  CompRef,
  DocumentServicesObject,
  CompData,
  SingleLayoutData,
  ComponentLayout,
} from '@wix/document-services-types';
import {
  Item,
  AppInstallOption,
} from '@wix/editor-platform-host-integration-apis';
import {
  PlatformContext,
  ExtendedPlatformContext,
} from '../../../../types/platformApi';
import { applyPresets } from './addComponents/addBuilderComponent';
import {
  createUnifiedComponentsInstallationError,
  UnifiedComponentsInstallationErrorCode as ErrorType,
} from './errors';

export const chainPromisesAsync = <T>(
  array: T[],
  functionToApply: (item: T) => Promise<any>,
) =>
  array.reduce(
    (previousPromise, currentItem) =>
      previousPromise.then(() => functionToApply(currentItem)),
    Promise.resolve(),
  );

export const getSize = (size?: WidgetSize) => ({
  height:
    size?.height?.heightMode === HeightMode.FIXED && size?.height?.defaultHeight
      ? size?.height?.defaultHeight
      : 500,
  width: size?.width?.defaultWidth ?? 500,
  ...(size?.width?.stretchByDefault
    ? {
        docked: {
          left: { px: 0, vw: 0 },
          right: { px: 0, vw: 0 },
        },
      }
    : {}),
});

export const cleanPresetIdPrefix = (presetId: string) =>
  presetId.replace(/^#+/g, '');

export const getSizeValue = (unitSize: UnitSize | number): number =>
  typeof unitSize === 'number' ? unitSize : unitSize.value;

export const getUnitSize = (
  sizeValue: UnitSize | number,
): UnitSize & { type: 'px' } =>
  typeof sizeValue !== 'number'
    ? { ...sizeValue, type: 'px' }
    : { value: sizeValue, type: 'px' };

export function initialSizeSettingToUnitSize(
  setting: InitialSizeSetting | undefined,
): UnitSize | undefined {
  if (!setting) {
    return undefined;
  }

  switch (setting.sizingType) {
    case 'pixels':
      return { type: 'px', value: setting.pixels ?? 0 };
    case 'content':
      // No 'fitContent' type, fallback to 'px' with a default value (e.g., 100)
      return { type: 'px', value: setting.pixels ?? 100 };
    case 'stretched':
      return { type: 'percentage', value: 100 };
    default:
      return undefined;
  }
}

/**
 * Converts a ComponentInitialSize to { width: UnitSize, height: UnitSize }
 * Falls back to provided defaults if not set.
 */
export function componentInitialSizeToUnitSize(
  initialSize: ComponentInitialSize | undefined,
  fallback: { width: UnitSize; height: UnitSize },
): { width: UnitSize; height: UnitSize } {
  return {
    width: initialSizeSettingToUnitSize(initialSize?.width) ?? fallback.width,
    height:
      initialSizeSettingToUnitSize(initialSize?.height) ?? fallback.height,
  };
}

export function createLayouts(
  componentLayout: ComponentLayout,
): Partial<SingleLayoutData> {
  return {
    componentLayout,
    containerLayout: {
      type: 'GridContainerLayout',
      rows: [{ type: 'fr', value: 1 }],
      columns: [{ type: 'fr', value: 1 }],
    },
    itemLayout: {
      id: '',
      type: 'GridItemLayout',
      gridArea: {
        columnStart: 1,
        columnEnd: 2,
        rowStart: 1,
        rowEnd: 2,
      },
      alignSelf: 'center',
      justifySelf: 'center',
      margins: {
        left: { type: 'percentage', value: 0 },
        top: { type: 'px', value: 0 },
      },
    },
    type: 'SingleLayoutData',
  };
}

export const getFixedPosition = (
  containerLayout: CompLayout | null,
  widgetSize: Item['size'],
  position?: Point,
) => {
  if (position) {
    return position;
  }

  const isStretched = widgetSize.docked !== undefined;

  if (containerLayout !== null && !isStretched) {
    return {
      x: Math.max((containerLayout.width - widgetSize.width) / 2, 0),
      y: 0,
    } as Point;
  }

  return { x: 0, y: 0 } as Point;
};

export const getInstallationPresetIdByBreakpoint = ({
  widgetDefinition,
  presetIdKey = DefaultPresetKeys.LARGE,
  overridePreset,
  shouldReadDataFromNewSchema,
}: PresetsOptions): string | undefined => {
  const deprecatedPresetIdKey =
    presetIdKey === DefaultPresetKeys.SMALL
      ? 'mobilePresetId'
      : 'desktopPresetId';

  const breakpointPresetId =
    presetIdKey === DefaultPresetKeys.SMALL
      ? widgetDefinition.presets?.[0]?.breakpoints?.small?.presetId
      : widgetDefinition.presets?.[0]?.id;

  const deprecatedPreset =
    widgetDefinition.installation?.widget?.defaultPreset?.[
      deprecatedPresetIdKey
    ];

  const installationPreset = shouldReadDataFromNewSchema
    ? widgetDefinition.installation?.presets?.[presetIdKey] ?? deprecatedPreset
    : deprecatedPreset;

  return (
    overridePreset?.[deprecatedPresetIdKey] ??
    installationPreset ??
    breakpointPresetId
  );
};

export function applyPresetsToAppComponents(
  context: PlatformContext,
  documentServices: DocumentServicesObject,
  appDefinitionId: string,
): void {
  const ooiCompData =
    documentServices.tpaV2.app.getAllComps([appDefinitionId]) ?? [];

  const ooiCompRefs: CompRef[] = ooiCompData
    .map((comp) => documentServices.components.get.byId(comp.id))
    .filter((compRef: CompRef | null): compRef is CompRef => compRef !== null);

  ooiCompRefs.forEach((compRef) => {
    try {
      applyPresets(context, compRef);
    } catch (error) {
      console.error('Failed to apply installation presets', compRef, error);
    }
  });
}

export function runEditorScript(
  documentServices: DocumentServicesObject,
  appDefinitionId: string,
  appOptions: AppInstallOption | undefined,
): Promise<void> {
  return new Promise<void>((resolve, reject) =>
    documentServices.platform.installationFlow.addToDocumentWithEditorScript(
      appDefinitionId,
      {
        [appDefinitionId]: appOptions,
        singleAppCallback: () => resolve(),
        onError: (error: Error) =>
          reject(
            createUnifiedComponentsInstallationError(
              ErrorType.runningEditorScriptFailed,
              'Could not run editor script',
            )
              .withAppDefIds(appDefinitionId)
              .withParentError(error),
          ),
      },
    ),
  );
}

export function isStudioBasedEditor() {
  return ['studio', 'studio2', 'editor3'].includes(
    window.commonConfig?.host?.toLowerCase() ?? '',
  );
}

export function getLayouts(
  context: ExtendedPlatformContext,
  options?: {
    initialSize?: ComponentInitialSize;
    containerRef?: CompRef;
    staticContainer?: StaticContainer;
  },
) {
  const { initialSize, containerRef, staticContainer } = options ?? {};
  const parentPointer =
    containerRef && staticContainer !== StaticContainer.GLOBAL_PAGE
      ? context.editorPlatformPackagesBridge.EditorPointerAPI?.buildEditorPointer(
          containerRef,
        )
      : undefined;

  let layouts = context.platformApiMethods.editor.generateComponentLayout?.({
    componentInitialSize: initialSize,
    parentPointer,
  });
  if (!layouts) {
    const size = componentInitialSizeToUnitSize(initialSize, {
      width: { value: 11.225296442687746, type: 'percentage' },
      // @ts-expect-error - TODO: fix type in DM
      height: { value: 0.2943661971830986, type: 'aspectRatio' },
    });

    layouts = createLayouts({
      type: 'ComponentLayout',
      width: size.width,
      height: size.height,
      minWidth: {
        type: 'px',
        value: 0,
      },
      minHeight: {
        type: 'unset',
      },
      maxWidth: {
        type: 'px',
        value: 99999,
      },
      maxHeight: {
        type: 'px',
        value: 99999,
      },
      hidden: false,
    });
  }
  return layouts;
}
